import { DATA_ROOM_STATIC_ID } from "@/components/data-room/DataRoomCategoriesDefines";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import PagedResult from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/PagedResult";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import { DataRoomFileAIAnalysis } from "@/models/data-room/DataRoomFileAIAnalysis";
import { DataRoomEntry } from "@/pages/OneDataRoomPage";
import { download } from "@/utils/DownloadManyFilesZip";
import {
  DATA_ROOM_CUSTOM_FOLDER_EVENTS,
  DataRoomCustomDeletedFilesEvent,
  DataRoomCustomMovedFilesEvent,
} from "./DataRoomCustomFolderService";
import { convertBase64ToFile } from "./LazoFileService";

class DataRoomService {
  path: string;

  constructor(path: string) {
    this.path = path;
  }

  uploadFiles(files: File[], tenantExternalId: string, staticId?: DATA_ROOM_STATIC_ID): Promise<ApiResponseV2<DataRoomFile[]>> {
    let qryString = `?tenantExternalId=${tenantExternalId}`;
    if (staticId) {
      qryString += `&parentId=${staticId}`;
    }
    const formData = new FormData();
    files.forEach((f) => {
      formData.append("files", f);
    });
    return HttpClientV2.post(`${this.path}/upload-files${qryString}`, formData, {
      axiosConfig: {
        timeout: 60000,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    });
  }

  getFiles(staticId: DATA_ROOM_STATIC_ID, tenantExternalId: string): Promise<ApiResponseV2<DataRoomFile[]>> {
    return HttpClientV2.get(`${this.path}/files?parentId=${staticId}&tenantExternalId=${tenantExternalId}`);
  }

  /// Moves files to a static folder,
  /// if a fromExternalId is provided it means
  /// it was moved from a custom folder so notify the custom tree
  moveFiles(fileExternalIds: string[], destination: DATA_ROOM_STATIC_ID, fromExternalId?: string): Promise<ApiResponseV2<string>> {
    return new Promise<ApiResponseV2<string>>((res, rej) => {
      HttpClientV2.post<any, string>(`${this.path}/move/files`, {
        fileExternalIds,
        destination,
      })
        .then((r) => {
          // dispatch event only if the from folder was custom
          if (fromExternalId) {
            const eventPayload: DataRoomCustomMovedFilesEvent = {
              count: fileExternalIds.length,
              fromExternalId: fromExternalId,
            };
            document.dispatchEvent(new CustomEvent(DATA_ROOM_CUSTOM_FOLDER_EVENTS.MOVED_FILES, { detail: eventPayload }));
          }

          res(r);
        })
        .catch(rej);
    });
  }

  downloadFiles(fileExternalIds: string[], entryName: string): Promise<void> {
    if (fileExternalIds.length > 1) {
      entryName += ".zip";
    }
    return new Promise((res, rej) => {
      HttpClientV2.post<{ fileExternalIds: string[] }, string>(
        `${this.path}/download/files`,
        {
          fileExternalIds,
        },
        {
          axiosConfig: {
            timeout: 60000,
          },
        }
      ).then((r) => {
        r.onSuccess((c) => {
          download(convertBase64ToFile(c, "file"), entryName);
          res();
        });
        r.onError((e) => {
          rej(e);
        });
      });
    });
  }

  /// When fromExternalId is passed down it means that the file was deleted
  // from a custom folder so we dispatch an event to notify this
  deleteFiles(fileExternalIds: string[], fromExternalId?: string): Promise<ApiResponseV2<void>> {
    return new Promise<ApiResponseV2<void>>((res, rej) => {
      HttpClientV2.post<any, void>(`${this.path}/delete/files`, fileExternalIds)
        .then((r) => {
          r.onSuccess(() => {
            if (fromExternalId) {
              const eventPayload: DataRoomCustomDeletedFilesEvent = {
                count: fileExternalIds.length,
                fromExternalId: fromExternalId,
              };

              const ev = new CustomEvent(DATA_ROOM_CUSTOM_FOLDER_EVENTS.DELETED_FILES, { detail: eventPayload });

              document.dispatchEvent(ev);
            }
          });

          res(r);
        })
        .catch(rej);
    });
  }

  approveFiles(fileExternalIds: string[]): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/approve/files`, fileExternalIds, {
      axiosConfig: { timeout: 30000 },
    });
  }

  getPreSignedUrl(fileExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/presigned-url?externalId=${fileExternalId}`);
  }

  getStats(tenantExternalId: string, dto: DataRoomEntry): Promise<ApiResponseV2<DataRoomEntry>> {
    return HttpClientV2.post(`${this.path}/stats?tenantExternalId=${tenantExternalId}`, dto);
  }

  updateSummary(fileExternalId: string, dto: DataRoomFileAIAnalysis): Promise<ApiResponseV2<DataRoomFile>> {
    return HttpClientV2.put(`${this.path}/update/summary?fileExternalId=${fileExternalId}`, dto);
  }

  importFilesDocumentCenter(externalIds: string[]): Promise<ApiResponseV2<DataRoomFile[]>> {
    return HttpClientV2.post(
      `${this.path}/import/document-center`,
      {
        fileExternalIds: externalIds,
      },
      {
        axiosConfig: {
          timeout: 60000,
        },
      }
    );
  }

  search(t: string, tenantExternalId: string): Promise<ApiResponseV2<PagedResult<DataRoomFile>>> {
    return HttpClientV2.get(`${this.path}/search?tenantExternalId=${tenantExternalId}&t=${encodeURIComponent(t)}`);
  }

  getByDocumentId(id: number, tenantExternalId: string): Promise<ApiResponseV2<DataRoomFile>> {
    return HttpClientV2.get(`${this.path}/file?id=${id}&tenantExternalId=${tenantExternalId}`);
  }

  getByDocumentExternalId(id: string): Promise<ApiResponseV2<DataRoomFile>> {
    return HttpClientV2.get(`${this.path}/file-by-external?externalId=${id}`);
  }
}

export default new DataRoomService("/api/data-room");
